import { Injectable } from '@angular/core';
import { HttpAuthHelperService } from './http-auth-helper.service';
import { environment } from 'src/environments/environment';
import { Project } from '../entities/organization.entity';
import { BehaviorSubject, Observable } from 'rxjs';
import { UAMAuthService } from './uam-auth.service';
import { isPublicRoute } from '../helpers/public-route.helper';

export type StandaloneFormTheme = 'warfighter' | 'pavcon' | 'safire';

export interface StandaloneFormAccessRef {
  formUUID: string;
  uamAssignmentUUID: string;
  theme?: StandaloneFormTheme;
}

export interface OrganizationProjectsRow {
  organization: string;
  projects: string[];
}

export interface WarfighterUserData {
  uuid: string;
  name: string;
  lastName: string;
  email: string;
  organization: string;
  warfighter_role: string;
  projects: string[];
  standaloneForms?: Array<string | StandaloneFormAccessRef>;
  formsInsightHubAccess?: string[];
  isLocked: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AppUserService {
  private baseUrl = environment.endpoint;
  private selectedProject = new BehaviorSubject<string | null>(null);

  constructor(
    private httpAuthHelperService: HttpAuthHelperService,
    private uamAuthService: UAMAuthService
  ) {}

  setSelectedProject(project: string): void {
    this.selectedProject.next(project);
  }
  getSelectedProject(): Observable<string | null> {
    return this.selectedProject.asObservable();
  }

  async getUserDetails(): Promise<any> {
    if (isPublicRoute()) {
      return Promise.reject({
        success: false,
        message: 'User settings not available on public routes',
      });
    }

    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise('GET', `${this.baseUrl}/settings/get-settings`, {})
        .then((data) => {
          if (data && data.body && data.body.response) {
            resolve(data.body.response);
          }
          reject({ success: false, message: 'Missing Settings' });
        })
        .catch((error) => {
          reject({ success: false, message: 'Missing Settings', error });
        });
    });
  }
  getAuthenticatedUser() {
    return this.uamAuthService.getAuthenticatedUser();
  }
  getOrganizationProjects(): Promise<string[]> {
    return new Promise<string[]>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise('GET', `${this.baseUrl}/settings/get-projects`, {})
        .then((data) => {
          const organizationProjects = data.body.response.reduce(
            (acc: Record<string, Project[]>, item: Project) => {
              if (!acc[item.organization]) {
                acc[item.organization] = [];
              }
              acc[item.organization].push(item);
              return acc;
            },
            {}
          );
          const org = Object.keys(organizationProjects);
          if (org.length === 1) {
            resolve(organizationProjects[org[0]]);
          } else {
            resolve(organizationProjects);
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getWarfigtherUsers() {
    return new Promise<string[]>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'GET',
          `${this.baseUrl}/settings/get-warfighter-users`,
          {}
        )
        .then((data) => {
          if (data && data.body && data.body.response) {
            resolve(data.body.response);
          } else {
            reject({ success: false, message: 'Missing Warfighter Users' });
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getStandaloneForms() {
    return new Promise<any[]>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'GET',
          `${this.baseUrl}/settings/get-standalone-forms`,
          {}
        )
        .then((data) => {
          if (data && data.body && data.body.response) {
            resolve(data.body.response);
          } else {
            reject({
              success: false,
              message: 'Error getting standalone forms',
            });
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
  getAllAvailableForms() {
    return new Promise<any[]>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'GET',
          `${this.baseUrl}/settings/get-all-available-forms`,
          {}
        )
        .then((data) => {
          if (data && data.body && data.body.response) {
            resolve(data.body.response);
          } else {
            reject({
              success: false,
              message: 'Error getting standalone forms',
            });
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }


  updateWarfigtherUsersV2(
    userUUID: string,
    organization: string,
    projects: string[],
    standaloneForms: StandaloneFormAccessRef[] = [],
    formsInsightHubAccess: string[] = []
  ) {
    return new Promise<string[]>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/settings/update-warfighter-users`,
          {
            userUUID,
            organization,
            projects,
            standaloneForms,
            formsInsightHubAccess,
          }
        )
        .then((data) => {
          if (data && data.body && data.body.response) {
            resolve(data.body.response);
          } else {
            reject({ success: false, message: 'Missing Warfighter Users' });
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
}
